// Reusable PDP-audit tool: URL form → animated progress → report, with the
// full error mapping for /public/audit/pdp-shaped endpoints. Extracted from
// src/pages/publicPdpAudit/PdpAuditPage.tsx so the RMN white-label portal
// can run the same tool against /rmn/audit/pdp (bearer auth, no captcha,
// no email, no daily limit). The public page is a thin wrapper that keeps
// its historical behavior (captcha, email when logged out, 3/day limit,
// ?bg= override, LinkedIn conversion).

import React, { useCallback, useRef, useState } from "react";
import { Alert } from "@mui/material";
import axiosInstant from "../../api";
import { apiErrorCode } from "../../utils/apiError";
import { TurnstileWidget } from "./TurnstileWidget";
import { PdpAuditForm, PdpAuditFormSubmitArgs } from "./PdpAuditForm";
import { PdpAuditReport } from "./PdpAuditReport";
import { PdpAuditResponse } from "./types";
import { useAuditSubmissionLimit } from "./useAuditSubmissionLimit";
import { useUser } from "../../hooks/useUser";
import { buildRegisterHandoffPath, RegisterHandoffInput } from "../../utils/funnelHandoff";
import { BLACK, BORDER, FONT, GOLD, GOLD_LIGHT, GOLD_TINT, GREEN, GREEN_BG, PAGE_BG, SURFACE_2, TEXT_DISCLAIMER, TEXT_FAINT, TEXT_MUTED } from "../ProposalFlow/theme";
import { PRIVACY_LABEL, PRIVACY_URL, TERMS_LABEL, TERMS_URL } from "../../config/aiDisclaimer";

const LOADING_IMAGE_URL =
    "https://images.squarespace-cdn.com/content/v1/67e552c1736de526d8d30758/546eebed-bb9c-433e-9e14-a4b97daf3ce3/Group+1171276328.png?format=500w";

const PROGRESS_MAX_DURATION_MS = 150000;

// Once the API responds we want the progress bar to glide to 100% before the
// report swaps in — otherwise the loader hops from ~30% straight to the
// success view. This is how long that final glide takes.
const PROGRESS_FINISH_DURATION_MS = 700;

// TDC-1591 automatic retry: when the request dies in transit (load-balancer
// cut, express RequestTimeout) the audit keeps running server-side and
// records the proxy tier that works for the domain, so a fresh attempt
// reconnects cheaply instead of bouncing the user back to the form.
const MAX_AUDIT_ATTEMPTS = 3;
const RETRY_DELAY_MS = 2000;
// The public endpoint needs a fresh (single-use) Turnstile token per retry —
// how long we wait for the re-mounted widget to mint one before giving up.
const RETRY_TOKEN_WAIT_MS = 45000;

const DEFAULT_HEADING = "Product Page Audit";

type PageState =
    | { kind: "idle" }
    | { kind: "submitting"; url: string; attempt: number; awaitingToken: boolean }
    | { kind: "finishing"; url: string; data: PdpAuditResponse }
    | { kind: "success"; data: PdpAuditResponse }
    | { kind: "error"; message: string }
    // ipLimit: our own per-IP limiter (rate_limit_exceeded) as opposed to
    // some other 429 — only that one earns the create-account prompt.
    // url/email: the blocked submission, carried into the register link so
    // the product still comes along (TDC-1573).
    | { kind: "rateLimited"; message: string; ipLimit: boolean; url?: string; email?: string }
    | { kind: "dailyLimitReached"; url?: string; email?: string }
    | { kind: "captchaFailed" };

// Transport-level deaths only: no response at all (network drop, LB cut,
// client abort) or the API's own express-ceiling RequestTimeout / gateway
// 504/408. Typed audit failures are final — FetchFailed means the whole
// proxy ladder was exhausted, and quota/captcha/4xx won't improve on retry.
function isRetryableAuditError(err: any): boolean {
    if (!err?.response) return true;
    const status = err.response.status;
    if (status === 504 || status === 408) return true;
    return apiErrorCode(err) === "RequestTimeout";
}

// Maps a failed audit POST to the terminal PageState. Check specific error
// codes first; HTTP status alone overlaps across multiple failure modes
// (e.g. 429 = our IP rate limit OR Gemini quota; 503 = AI unavailable OR
// scraper credits out). Typed codes arrive as a bare string body on
// control()-wrapped routes (/rmn/*) and as { error } elsewhere.
function mapAuditError(err: any): PageState {
    const status = err?.response?.status;
    const errorBody = err?.response?.data;
    const code = apiErrorCode(err);
    if (code === "AiQuotaExhausted") {
        return {
            kind: "error",
            message:
                "Our daily AI quota has been reached. Please try again tomorrow or contact sales@thedesirecompany.com."
        };
    }
    if (code === "ScraperOutOfCredits") {
        return {
            kind: "error",
            message:
                "Our page-fetch service has reached its monthly limit. Please try again later or contact sales@thedesirecompany.com."
        };
    }
    if (code === "ScraperAuthFailed") {
        return {
            kind: "error",
            message:
                "Our page-fetch service is misconfigured. We've been notified — please try again later."
        };
    }
    if (code === "ScraperConcurrencyLimit") {
        return {
            kind: "error",
            message: "Too many audits running right now. Please try again in a moment."
        };
    }
    if (code === "DomainNotAllowed") {
        return {
            kind: "error",
            message:
                "That URL isn't on this network's allowed domains. Audit a product page on your retail network's site."
        };
    }
    if (code === "rate_limit_exceeded" || status === 429) {
        return {
            kind: "rateLimited",
            ipLimit: code === "rate_limit_exceeded",
            message: errorBody?.message || "Too many audits from this network. Please try again later."
        };
    }
    if (status === 400 && code === "CaptchaFailed") {
        return { kind: "captchaFailed" };
    }
    if (status === 400 && code === "InvalidUrl") {
        return {
            kind: "error",
            message: "We couldn't read that URL. Double-check it points to a public product page."
        };
    }
    if (status === 400 && code === "EmailRequired") {
        return {
            kind: "error",
            message: "Please enter a company email address to run the audit."
        };
    }
    if (status === 400 && code === "EmailDomainNotAllowed") {
        return {
            kind: "error",
            message:
                "Please use a company email address — consumer providers (gmail, yahoo, etc.) aren't supported."
        };
    }
    if (status === 400 && code === "EmailDomainMismatch") {
        return {
            kind: "error",
            message: "Your email must be from the same domain as the URL you're auditing."
        };
    }
    if (status === 502 || code === "FetchFailed") {
        return {
            kind: "error",
            message:
                "We couldn't load that page (it may be blocking automated requests). Try a different product URL."
        };
    }
    if (status === 503 || code === "AiUnavailable" || code === "RequestTimeout") {
        return {
            kind: "error",
            message: "Our service is in high demand right now. Please try again in a few minutes."
        };
    }
    return {
        kind: "error",
        message: errorBody?.message || "Something went wrong generating your audit. Please try again."
    };
}

export interface PdpAuditToolProps {
    endpoint: string;
    showCaptcha: boolean;
    requireEmail: boolean;
    enforceDailyLimit: boolean;
    showPortalCta?: boolean;
    heading?: string;
    // Shell/report background. Defaults to the theme page background
    // (var(--rmn-bg, #FAFAF8)); the public page passes its ?bg= override.
    bgColor?: string;
    // Fired when a report arrives (as the progress bar completes and the
    // report view mounts). Public page uses it for LinkedIn conversion
    // tracking; the RMN page refreshes its past-audits list.
    onReportCreated?: (report: PdpAuditResponse) => void;
    // "page" fills the viewport; "modal" drops the min-height so the whole
    // form → progress → report flow can live inside a Dialog.
    variant?: "page" | "modal";
    // Passed through to the report's Create Proposal CTA — the RMN portal
    // points it at its white-label wizard (/portal/:brandSlug/proposal).
    proposalPath?: string;
    // CTA label pass-through (brand portal: "Create Strategy").
    proposalCtaLabel?: string;
    // Passed through to the report — the RMN portal hides the email action.
    showEmailAction?: boolean;
}

function Heading({ heading }: { heading: string }) {
    return (
        <>
            <h1
                style={{
                    fontFamily: FONT,
                    fontSize: 32,
                    fontWeight: 700,
                    color: BLACK,
                    letterSpacing: "-0.3px",
                    margin: "0 0 10px"
                }}
            >
                {heading}
            </h1>
            <p
                style={{
                    fontFamily: FONT,
                    fontSize: 15,
                    color: TEXT_MUTED,
                    lineHeight: 1.6,
                    margin: "0 0 32px"
                }}
            >
                Drop in a product detail page URL and we'll score it on Human Conversion Readiness and Agentic Commerce
                Readiness.
            </p>
        </>
    );
}

function FormBody({
    heading,
    disabled,
    remaining,
    requireEmail,
    showCaptcha,
    showPortalCta,
    bypassed,
    onSubmit,
    children
}: {
    heading: string;
    disabled: boolean;
    remaining: number;
    requireEmail: boolean;
    showCaptcha: boolean;
    showPortalCta: boolean;
    bypassed: boolean;
    onSubmit: (a: PdpAuditFormSubmitArgs) => Promise<void>;
    children?: React.ReactNode;
}) {
    return (
        <div style={{ width: 640, maxWidth: "100%", margin: "0 auto", padding: "48px 20px" }}>
            <Heading heading={heading} />

            {children}

            <PdpAuditForm onSubmit={onSubmit} showCaptcha={showCaptcha} disabled={disabled} requireEmail={requireEmail} />

            {!bypassed && (
                <div
                    style={{
                        fontFamily: FONT,
                        textAlign: "center",
                        fontSize: 13,
                        color: TEXT_FAINT,
                        marginTop: 14
                    }}
                >
                    <strong style={{ color: BLACK, fontWeight: 600 }}>{remaining}</strong> of 3 audits remaining today
                </div>
            )}

            {showPortalCta && <PortalCta />}

            <AuditDisclaimer />
        </div>
    );
}

// The audit-limit account prompt (TDC-1573). Copy is product-owned — keep
// it verbatim.
export const LIMIT_ACCOUNT_PROMPT =
    "Create an account in order to audit more products, get strategies, PDP enhancements and more. Audit 1 product ongoing for free. Test it out.";

export function AccountPromptAlert({
    handoff,
    severity = "info"
}: {
    handoff?: RegisterHandoffInput;
    severity?: "info" | "warning";
}) {
    return (
        <Alert severity={severity} sx={{ mt: 2 }}>
            {LIMIT_ACCOUNT_PROMPT}{" "}
            <a
                href={buildRegisterHandoffPath(handoff)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, fontWeight: 600, whiteSpace: "nowrap" }}
            >
                Create your free account →
            </a>
        </Alert>
    );
}

// With a handoff (post-report) the link carries the audited product so
// registration imports it as the brand's first scored product; the idle
// form instance has nothing to hand off and stays a plain /register link.
export function PortalCta({ handoff }: { handoff?: RegisterHandoffInput }) {
    const hasHandoff = !!handoff && !!(handoff.url || handoff.email);
    return (
        <p
            style={{
                fontFamily: FONT,
                fontSize: 12,
                color: TEXT_MUTED,
                textAlign: "center",
                marginTop: 24,
                marginBottom: 0
            }}
        >
            <a
                href={buildRegisterHandoffPath(handoff)}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, textDecoration: "none" }}
            >
                {hasHandoff ? "Create your free account" : "Create an account in our portal"}
            </a>{" "}
            {hasHandoff
                ? "to keep this product scored weekly and build strategies — or email"
                : "to request a quote — or email"}{" "}
            <a
                href="mailto:sales@thedesirecompany.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: GOLD, textDecoration: "none" }}
            >
                sales@thedesirecompany.com
            </a>
            .
        </p>
    );
}

export function AuditDisclaimer() {
    return (
        <div
            style={{
                fontFamily: FONT,
                display: "flex",
                gap: 10,
                alignItems: "flex-start",
                marginTop: 32,
                paddingTop: 16,
                borderTop: `1px solid ${BORDER}`
            }}
        >
            <img
                src="/logo-transparent-tdc-only.png"
                alt="The Desire Company"
                style={{ width: 22, height: 22, objectFit: "contain", flexShrink: 0, marginTop: 2 }}
            />
            <p
                style={{
                    fontSize: 11.5,
                    color: TEXT_DISCLAIMER,
                    lineHeight: 1.6,
                    margin: 0
                }}
            >
                <strong style={{ color: TEXT_FAINT }}>The Desire Company</strong> — The PDP audit and
                proposal tools utilize The Desire Company&apos;s AI and draw on publicly available
                information from the URL you submitted. Scores and findings are informational, may contain
                inaccuracies, and are not professional advice. The Desire Company is not affiliated with,
                endorsed by, or sponsored by the brands or retailers analyzed. Brand and product names are
                referenced for identification only; all trademarks belong to their respective owners. Use of
                this tool constitutes consent to our{" "}
                <a
                    href={TERMS_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: TEXT_FAINT, textDecoration: "underline" }}
                >
                    {TERMS_LABEL}
                </a>
                {" and "}
                <a
                    href={PRIVACY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: TEXT_FAINT, textDecoration: "underline" }}
                >
                    {PRIVACY_LABEL}
                </a>
                .
            </p>
        </div>
    );
}

const STEPS: Array<{ label: string; start: number; end: number }> = [
    { label: "Fetching page content", start: 0, end: 20 },
    { label: "Analyzing product information", start: 20, end: 45 },
    { label: "Evaluating conversion readiness", start: 45, end: 70 },
    { label: "Scoring agentic commerce signals", start: 70, end: 90 },
    { label: "Generating report", start: 90, end: 100 }
];

function StepRow({ label, progress, start, end }: { label: string; progress: number; start: number; end: number }) {
    const status: "done" | "active" | "pending" = progress >= end ? "done" : progress >= start ? "active" : "pending";

    const dotBorder = status === "done" ? GREEN : status === "active" ? GOLD : BORDER;
    const dotBg = status === "done" ? GREEN_BG : status === "active" ? GOLD_TINT : "transparent";
    const labelColor = status === "done" ? GREEN : status === "active" ? BLACK : TEXT_FAINT;

    return (
        <div
            style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                fontFamily: FONT,
                fontSize: 13,
                color: labelColor,
                transition: "color 0.3s"
            }}
        >
            <div
                style={{
                    width: 20,
                    height: 20,
                    borderRadius: "50%",
                    border: `1.5px solid ${dotBorder}`,
                    background: dotBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    transition: "all 0.3s"
                }}
            >
                {status === "done" && (
                    <svg
                        width="10"
                        height="10"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke={GREEN}
                        strokeWidth="3"
                        strokeLinecap="round"
                    >
                        <polyline points="20 6 9 17 4 12" />
                    </svg>
                )}
                {status === "active" && (
                    <span
                        style={{
                            width: 10,
                            height: 10,
                            border: `1.5px solid ${BORDER}`,
                            borderTopColor: GOLD,
                            borderRadius: "50%",
                            animation: "tdcSpin 0.8s linear infinite",
                            display: "inline-block"
                        }}
                    />
                )}
            </div>
            {label}
        </div>
    );
}

function LoadingBody({
    heading,
    url,
    dataReady,
    onComplete,
    retrying = false,
    extraContent = null
}: {
    heading: string;
    url: string;
    dataReady: boolean;
    onComplete: () => void;
    // TDC-1591: an automatic retry is in flight — swap the duration hint for
    // a patient message so the user knows why it's slow.
    retrying?: boolean;
    // Slot below the step list (the public page mounts a fresh Turnstile
    // widget here to mint the retry's captcha token).
    extraContent?: React.ReactNode;
}) {
    const [progress, setProgress] = useState(0);
    const [longRunning, setLongRunning] = useState(false);
    const startRef = useRef<number>(Date.now());
    const progressRef = useRef<number>(0);
    progressRef.current = progress;

    // Time-based animation up to a 98% cap while we're waiting for the API.
    React.useEffect(() => {
        if (dataReady) return;
        startRef.current = Date.now();
        const id = setInterval(() => {
            const elapsed = Date.now() - startRef.current;
            const t = Math.min(elapsed / PROGRESS_MAX_DURATION_MS, 1);
            const eased = 98 * (1 - Math.pow(1 - t, 2.5));
            setProgress(eased);
            if (elapsed > PROGRESS_MAX_DURATION_MS) {
                setLongRunning(true);
            }
        }, 80);
        return () => clearInterval(id);
    }, [dataReady]);

    // Once the API has answered, glide from wherever we are to 100% over a
    // fixed short duration, then signal the parent to swap to the report.
    React.useEffect(() => {
        if (!dataReady) return;
        const start = progressRef.current;
        const finishStart = Date.now();
        let completed = false;
        const id = setInterval(() => {
            const t = Math.min((Date.now() - finishStart) / PROGRESS_FINISH_DURATION_MS, 1);
            setProgress(start + (100 - start) * t);
            if (t >= 1 && !completed) {
                completed = true;
                clearInterval(id);
                onComplete();
            }
        }, 30);
        return () => clearInterval(id);
    }, [dataReady, onComplete]);

    return (
        <div style={{ width: 640, maxWidth: "100%", margin: "0 auto", padding: "48px 20px" }}>
            <Heading heading={heading} />

            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", paddingTop: 8 }}>
                <img
                    src={LOADING_IMAGE_URL}
                    alt="Analyzing your product page"
                    style={{ width: 320, maxWidth: "100%", marginBottom: 32, borderRadius: 16 }}
                />
                <div
                    style={{
                        fontFamily: FONT,
                        fontSize: 16,
                        fontWeight: 600,
                        color: BLACK,
                        marginBottom: 6,
                        textAlign: "center"
                    }}
                >
                    Analyzing your product page
                </div>
                <div
                    style={{
                        fontFamily: FONT,
                        fontSize: 13,
                        color: TEXT_FAINT,
                        marginBottom: 20,
                        textAlign: "center",
                        wordBreak: "break-all"
                    }}
                >
                    {url}
                </div>

                <div style={{ width: "100%", marginBottom: 8 }}>
                    <div
                        style={{
                            width: "100%",
                            height: 8,
                            background: SURFACE_2,
                            borderRadius: 4,
                            overflow: "hidden",
                            position: "relative"
                        }}
                    >
                        <div
                            style={{
                                height: "100%",
                                width: `${progress}%`,
                                background: `linear-gradient(90deg, ${GOLD}, ${GOLD_LIGHT})`,
                                borderRadius: 4,
                                transition: "width 0.15s ease-out"
                            }}
                        />
                    </div>
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            marginTop: 8
                        }}
                    >
                        <span
                            style={{
                                fontFamily: FONT,
                                fontSize: 13,
                                fontWeight: 600,
                                color: GOLD,
                                fontVariantNumeric: "tabular-nums"
                            }}
                        >
                            {Math.round(progress)}%
                        </span>
                        <span style={{ fontFamily: FONT, fontSize: 12, color: TEXT_DISCLAIMER, textAlign: "right" }}>
                            {retrying
                                ? "Taking longer than normal — retrying with a more thorough page fetch. Please be patient…"
                                : progress >= 100
                                    ? "Almost done…"
                                    : longRunning
                                        ? "This is taking longer than normal — please be patient, we're still working…"
                                        : "This may take up to 2 or 3 minutes"}
                        </span>
                    </div>
                </div>

                <div style={{ marginTop: 24, display: "flex", flexDirection: "column", gap: 8, width: "100%" }}>
                    {STEPS.map((s) => (
                        <StepRow key={s.label} label={s.label} progress={progress} start={s.start} end={s.end} />
                    ))}
                </div>
                {extraContent}
            </div>
            <style>{`@keyframes tdcSpin { to { transform: rotate(360deg); } }`}</style>
        </div>
    );
}

export function PdpAuditTool({
    endpoint,
    showCaptcha,
    requireEmail,
    enforceDailyLimit,
    showPortalCta = false,
    heading = DEFAULT_HEADING,
    bgColor = PAGE_BG,
    onReportCreated,
    variant = "page",
    proposalPath,
    proposalCtaLabel,
    showEmailAction
}: PdpAuditToolProps) {
    const [state, setState] = useState<PageState>({ kind: "idle" });
    const [submittedEmail, setSubmittedEmail] = useState<string>("");
    const { user } = useUser();
    const limit = useAuditSubmissionLimit({ enabled: enforceDailyLimit });
    const storeEmail =
        ((user as any)?.emailAddress as string | undefined) ||
        ((user as any)?.email_address as string | undefined) ||
        "";
    const isLoggedIn = !!user;

    // Retry plumbing (TDC-1591): timers + the email/attempt context a retry
    // needs, held in refs so the delayed re-POST and the Turnstile onToken
    // callback always see current values without re-rendering.
    const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const tokenWaitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const retryEmailRef = useRef<string>("");
    const consumedTokenAttemptRef = useRef<number>(-1);
    const runAuditRef = useRef<
        ((url: string, captchaToken: string | null, effectiveEmail: string, attempt: number) => Promise<void>) | null
    >(null);

    React.useEffect(
        () => () => {
            if (retryTimerRef.current) clearTimeout(retryTimerRef.current);
            if (tokenWaitTimerRef.current) clearTimeout(tokenWaitTimerRef.current);
        },
        []
    );

    const runAudit = useCallback(
        async (url: string, captchaToken: string | null, effectiveEmail: string, attempt: number) => {
            setState({ kind: "submitting", url, attempt, awaitingToken: false });
            try {
                const { data } = await axiosInstant.post<PdpAuditResponse>(endpoint, {
                    url,
                    captchaToken,
                    email: effectiveEmail || undefined
                });
                limit.recordSubmission(url);
                // Stay in the loading view but flag dataReady so the progress
                // bar can glide to 100% before the report mounts.
                setState({ kind: "finishing", url, data });
            } catch (err: any) {
                // The audit keeps running server-side after a transport death
                // and records the proxy tier that works for the domain
                // (TDC-1591), so an automatic fresh attempt reconnects
                // cheaply. Everything else maps to a terminal state.
                if (isRetryableAuditError(err) && attempt < MAX_AUDIT_ATTEMPTS - 1) {
                    if (showCaptcha) {
                        // Turnstile tokens are single-use — park in the
                        // loading view and let the freshly-mounted widget
                        // below the progress bar mint a new one; its onToken
                        // fires the retry. Bail out if no token arrives.
                        setState({ kind: "submitting", url, attempt: attempt + 1, awaitingToken: true });
                        tokenWaitTimerRef.current = setTimeout(() => {
                            setState((s) =>
                                s.kind === "submitting" && s.awaitingToken
                                    ? {
                                        kind: "error",
                                        message:
                                            "This is taking longer than normal and we couldn't retry automatically. Please run the audit again."
                                    }
                                    : s
                            );
                        }, RETRY_TOKEN_WAIT_MS);
                    } else {
                        setState({ kind: "submitting", url, attempt: attempt + 1, awaitingToken: false });
                        retryTimerRef.current = setTimeout(() => {
                            runAuditRef.current?.(url, null, effectiveEmail, attempt + 1);
                        }, RETRY_DELAY_MS);
                    }
                    return;
                }
                const mapped = mapAuditError(err);
                setState(
                    mapped.kind === "rateLimited" ? { ...mapped, url, email: effectiveEmail } : mapped
                );
            }
        },
        [endpoint, limit, showCaptcha]
    );
    runAuditRef.current = runAudit;

    const onSubmit = useCallback(
        async ({ url, captchaToken, email }: PdpAuditFormSubmitArgs) => {
            const effectiveEmail = (email || storeEmail || "").trim().toLowerCase();
            setSubmittedEmail(effectiveEmail);
            if (enforceDailyLimit && limit.isBlocked) {
                setState({ kind: "dailyLimitReached", url, email: effectiveEmail });
                return;
            }

            retryEmailRef.current = effectiveEmail;
            consumedTokenAttemptRef.current = -1;
            await runAudit(url, captchaToken, effectiveEmail, 0);
        },
        [enforceDailyLimit, limit, storeEmail, runAudit]
    );

    const reset = () => setState({ kind: "idle" });

    const handleProgressComplete = useCallback(() => {
        setState((s) => {
            if (s.kind !== "finishing") return s;
            if (onReportCreated) onReportCreated(s.data);
            return { kind: "success", data: s.data };
        });
    }, [onReportCreated]);

    const shellStyle: React.CSSProperties = {
        minHeight: variant === "modal" ? undefined : "100vh",
        background: bgColor,
        fontFamily: FONT
    };

    if (state.kind === "submitting" || state.kind === "finishing") {
        const retrying = state.kind === "submitting" && state.attempt > 0;
        return (
            <div style={shellStyle}>
                <FontLoader />
                <LoadingBody
                    heading={heading}
                    url={state.url}
                    dataReady={state.kind === "finishing"}
                    onComplete={handleProgressComplete}
                    retrying={retrying}
                    extraContent={
                        state.kind === "submitting" && state.awaitingToken ? (
                            <div
                                style={{
                                    marginTop: 24,
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "center",
                                    gap: 10,
                                    width: "100%"
                                }}
                            >
                                <div
                                    style={{
                                        fontFamily: FONT,
                                        fontSize: 13,
                                        color: TEXT_MUTED,
                                        textAlign: "center"
                                    }}
                                >
                                    Please confirm you're human so we can retry automatically.
                                </div>
                                <TurnstileWidget
                                    key={state.attempt}
                                    onToken={(token) => {
                                        if (!token) return;
                                        if (consumedTokenAttemptRef.current >= state.attempt) return;
                                        consumedTokenAttemptRef.current = state.attempt;
                                        if (tokenWaitTimerRef.current) {
                                            clearTimeout(tokenWaitTimerRef.current);
                                        }
                                        runAuditRef.current?.(
                                            state.url,
                                            token,
                                            retryEmailRef.current,
                                            state.attempt
                                        );
                                    }}
                                    theme="light"
                                    size="flexible"
                                />
                            </div>
                        ) : null
                    }
                />
            </div>
        );
    }

    if (state.kind === "success") {
        return (
            <div style={shellStyle}>
                <FontLoader />
                {state.data.meta.geminiStatus === "error" && (
                    <div
                        style={{
                            background: SURFACE_2,
                            paddingTop: 20
                        }}
                    >
                        <div
                            style={{
                                maxWidth: 720,
                                margin: "0 auto 0 auto",
                                padding: "0 20px"
                            }}
                        >
                            <Alert severity="warning">
                                {state.data.meta.geminiStatusCode === 429
                                    ? "AI processing is temporarily under heavy load. Some scores may be defaults — please regenerate later."
                                    : "AI processing encountered an issue. Some content may be incomplete."}
                            </Alert>
                        </div>
                    </div>
                )}
                <PdpAuditReport
                    data={state.data}
                    onAuditAnother={reset}
                    remainingToday={enforceDailyLimit ? limit.remaining : undefined}
                    bgColor={bgColor}
                    isLoggedIn={isLoggedIn}
                    auditEmail={submittedEmail}
                    proposalPath={proposalPath}
                    proposalCtaLabel={proposalCtaLabel}
                    showEmailAction={showEmailAction}
                />
                <div
                    style={{
                        maxWidth: 720,
                        margin: "0 auto",
                        padding: "24px 20px 48px"
                    }}
                >
                    {/* The report's own choice card carries the register
                        handoff for anonymous visitors. */}
                    <AuditDisclaimer />
                </div>
            </div>
        );
    }

    return (
        <div style={shellStyle}>
            <FontLoader />
            <FormBody
                heading={heading}
                disabled={state.kind === "dailyLimitReached"}
                remaining={limit.remaining}
                requireEmail={requireEmail}
                showCaptcha={showCaptcha}
                showPortalCta={showPortalCta}
                bypassed={!enforceDailyLimit}
                onSubmit={onSubmit}
            >
                {state.kind === "captchaFailed" && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        The captcha didn't verify. Please tick the checkbox above and try again.
                    </Alert>
                )}
                {state.kind === "rateLimited" && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                        {state.message}
                    </Alert>
                )}
                {state.kind === "rateLimited" && state.ipLimit && !isLoggedIn && (
                    <AccountPromptAlert handoff={{ email: state.email, url: state.url }} />
                )}
                {state.kind === "dailyLimitReached" && isLoggedIn && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        You've reached today's audit limit ({limit.used}/3). Come back tomorrow, or contact
                        sales@thedesirecompany.com to audit more pages.
                    </Alert>
                )}
                {/* Anonymous limit → account prompt (TDC-1573). Shown as soon as
                    the counter hits zero, not only after a blocked submit. */}
                {!isLoggedIn &&
                    (state.kind === "dailyLimitReached" || (state.kind === "idle" && limit.isBlocked)) && (
                        <AccountPromptAlert
                            handoff={{
                                email: state.kind === "dailyLimitReached" ? state.email : submittedEmail,
                                url: state.kind === "dailyLimitReached" ? state.url : undefined
                            }}
                        />
                    )}
                {state.kind === "error" && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {state.message}
                    </Alert>
                )}
            </FormBody>
        </div>
    );
}

export function FontLoader() {
    return (
        <style>{`@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,400;9..40,500;9..40,600;9..40,700&display=swap');`}</style>
    );
}
